import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, Calendar, Phone, MapPin, Building2, Clock, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import CreneauxDisponibles from './CreneauxDisponibles';
import SirenSearch from './SirenSearch';

const API = process.env.REACT_APP_BACKEND_URL;

const ProspectsPage = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRdvDialog, setShowRdvDialog] = useState(false);
  const [showCreneauxDialog, setShowCreneauxDialog] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [selectedProspectForRdv, setSelectedProspectForRdv] = useState(null);
  
  // États pour la recherche et les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('tous');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  
  const [formData, setFormData] = useState({
    adresse: '',
    telephone: '',
    nom: '',
    prenom: '',
    nom_entreprise: '',
    siren: '',
    email: '',
    type_energie: '',
    consommation_annuelle_euro: '',
    consommation_annuelle_kwh: '',
    fournisseur_actuel: '',
    notes: '',
    latitude: null,
    longitude: null,
    code_postal: '',
    ville: ''
  });

  // États pour le formulaire RDV
  const [rdvFormData, setRdvFormData] = useState({
    prospect_id: '',
    date_rdv: '',
    duree_minutes: 60,
    type_rdv: 'visite_commerciale',
    status: 'planifie',
    commentaire_rdv: ''
  });

  // États pour la recherche d'adresse (réutiliser la logique existante)
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);

  // États pour la recherche SIREN (réutiliser la logique existante)
  const [sirenSuggestions, setSirenSuggestions] = useState([]);
  const [showSirenSuggestions, setShowSirenSuggestions] = useState(false);
  const [isLoadingSiren, setIsLoadingSiren] = useState(false);

  useEffect(() => {
    // Récupérer l'utilisateur actuel
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error getting current user:', error);
    } finally {
      setUserLoaded(true);
    }
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/prospects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProspects(response.data);
    } catch (error) {
      console.error('Error fetching prospects:', error);
      toast.error('Erreur lors du chargement des prospects');
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddress = async (adresse, codePostal, ville) => {
    try {
      const fullAddress = `${adresse}, ${codePostal} ${ville}, France`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const handleCreateProspect = async () => {
    try {
      if (!formData.adresse || !formData.telephone) {
        toast.error('Adresse et téléphone sont obligatoires');
        return;
      }

      // Géolocalisation automatique
      const coords = await geocodeAddress(formData.adresse, formData.code_postal, formData.ville);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/prospects`, {
        ...formData,
        consommation_annuelle_euro: parseFloat(formData.consommation_annuelle_euro) || 0,
        consommation_annuelle_kwh: parseFloat(formData.consommation_annuelle_kwh) || 0,
        latitude: coords?.latitude || null,
        longitude: coords?.longitude || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Prospect créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchProspects();
    } catch (error) {
      console.error('Error creating prospect:', error);
      toast.error('Erreur lors de la création du prospect');
    }
  };

  const handleUpdateProspect = async () => {
    try {
      // Géolocalisation automatique
      const coords = await geocodeAddress(formData.adresse, formData.code_postal, formData.ville);

      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/prospects/${editingProspect.id}`, {
        ...formData,
        consommation_annuelle_euro: parseFloat(formData.consommation_annuelle_euro) || 0,
        consommation_annuelle_kwh: parseFloat(formData.consommation_annuelle_kwh) || 0,
        latitude: coords?.latitude || null,
        longitude: coords?.longitude || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Prospect modifié avec succès');
      setShowEditDialog(false);
      setEditingProspect(null);
      resetForm();
      fetchProspects();
    } catch (error) {
      console.error('Error updating prospect:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce prospect ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/prospects/${prospectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Prospect supprimé avec succès');
      fetchProspects();
    } catch (error) {
      console.error('Error deleting prospect:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      adresse: '',
      telephone: '',
      nom: '',
      prenom: '',
      nom_entreprise: '',
      siren: '',
      email: '',
      type_energie: '',
      consommation_annuelle_euro: '',
      consommation_annuelle_kwh: '',
      fournisseur_actuel: '',
      notes: '',
      latitude: null,
      longitude: null,
      code_postal: '',
      ville: ''
    });
  };

  const openEditDialog = (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      adresse: prospect.adresse,
      telephone: prospect.telephone,
      nom: prospect.nom || '',
      prenom: prospect.prenom || '',
      nom_entreprise: prospect.nom_entreprise || '',
      siren: prospect.siren || '',
      email: prospect.email || '',
      type_energie: prospect.type_energie || '',
      consommation_annuelle_euro: prospect.consommation_annuelle_euro || '',
      consommation_annuelle_kwh: prospect.consommation_annuelle_kwh || '',
      fournisseur_actuel: prospect.fournisseur_actuel || '',
      notes: prospect.notes || '',
      latitude: prospect.latitude || null,
      longitude: prospect.longitude || null,
      code_postal: prospect.code_postal || '',
      ville: prospect.ville || ''
    });
    setShowEditDialog(true);
  };

  // Recherche d'adresse (réutiliser la logique des clients)
  const searchAddresses = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      setAddressSuggestions(response.data.features || []);
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error('Error searching addresses:', error);
    }
  };

  const selectAddress = (address) => {
    const fullAddress = address.properties.label;
    setFormData({
      ...formData,
      adresse: fullAddress,
      code_postal: address.properties.postcode || '',
      ville: address.properties.city || '',
      latitude: address.geometry.coordinates[1] || 0,
      longitude: address.geometry.coordinates[0] || 0
    });
    setShowAddressSuggestions(false);
  };

  // Fonction pour gérer la sélection d'entreprise SIREN
  const handleCompanySelect = (companyData) => {
    setFormData(prevData => ({
      ...prevData,
      siren: companyData.siren,
      nom_entreprise: companyData.raison_sociale,
      adresse: companyData.adresse,
      code_postal: companyData.code_postal,
      ville: companyData.ville,
      // Remplir automatiquement le nom et prénom du dirigeant
      nom: companyData.nom,
      prenom: companyData.prenom
    }));

    toast.success(`Entreprise sélectionnée: ${companyData.raison_sociale}`);
  };

  // Recherche SIREN (réutiliser la logique des clients)
  const searchSiren = async (query) => {
    if (query.length < 3) {
      setSirenSuggestions([]);
      setShowSirenSuggestions(false);
      return;
    }

    setIsLoadingSiren(true);
    try {
      const response = await axios.get(
        `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&limite=5`
      );
      setSirenSuggestions(response.data.results || []);
      setShowSirenSuggestions(true);
    } catch (error) {
      console.error('Error searching SIREN:', error);
    } finally {
      setIsLoadingSiren(false);
    }
  };

  const selectSiren = (company) => {
    setFormData({
      ...formData,
      siren: company.siren,
      nom_entreprise: company.nom_complet,
      adresse: company.siege?.adresse || formData.adresse
    });
    setShowSirenSuggestions(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'nouveau': return 'bg-blue-100 text-blue-800';
      case 'rdv_planifie': return 'bg-orange-100 text-orange-800';
      case 'converti_client': return 'bg-green-100 text-green-800';
      case 'abandonne': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonctions pour la création de RDV
  const openRdvDialog = (prospect) => {
    setSelectedProspectForRdv(prospect);
    setRdvFormData({
      prospect_id: prospect.id,
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      commentaire_rdv: ''
    });
    setShowRdvDialog(true);
  };

  const openCreneauxDialog = () => {
    if (!selectedProspectForRdv || !selectedProspectForRdv.latitude || !selectedProspectForRdv.longitude) {
      toast.error('Adresse du prospect non géolocalisée. Impossible de calculer les créneaux optimaux.');
      return;
    }
    setShowCreneauxDialog(true);
  };

  const handleSelectCreneau = (creneauData) => {
    setRdvFormData({
      ...rdvFormData,
      date_rdv: creneauData.date_rdv.slice(0, 16), // Format pour input datetime-local
    });
    
    toast.success(`Créneau sélectionné: ${new Date(creneauData.date_rdv).toLocaleDateString('fr-FR')} à ${new Date(creneauData.date_rdv).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})} avec ${creneauData.suggested_commercial.first_name} ${creneauData.suggested_commercial.last_name}`);
  };

  const handleCreateRdv = async () => {
    try {
      if (!rdvFormData.prospect_id || !rdvFormData.date_rdv) {
        toast.error('Prospect et date sont obligatoires');
        return;
      }

      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/api/rendez-vous`, rdvFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mettre à jour le statut du prospect
      await axios.put(`${API}/api/prospects/${selectedProspectForRdv.id}`, {
        ...selectedProspectForRdv,
        status: 'rdv_planifie'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('🎉 Rendez-vous créé avec succès !');
      setShowRdvDialog(false);
      setShowCreneauxDialog(false);
      setSelectedProspectForRdv(null);
      resetRdvForm();
      fetchProspects(); // Refresh la liste
      
    } catch (error) {
      console.error('Error creating rendez-vous:', error);
      toast.error('Erreur lors de la création du rendez-vous');
    }
  };

  const resetRdvForm = () => {
    setRdvFormData({
      prospect_id: '',
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      commentaire_rdv: ''
    });
  };

  // Fonction de filtrage des prospects
  const getFilteredProspects = () => {
    let filtered = [...prospects];

    // Filtre par recherche (nom, prénom, entreprise, SIREN, téléphone)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(prospect =>
        prospect.nom?.toLowerCase().includes(search) ||
        prospect.prenom?.toLowerCase().includes(search) ||
        prospect.nom_entreprise?.toLowerCase().includes(search) ||
        prospect.siren?.includes(search) ||
        prospect.telephone?.includes(search) ||
        prospect.adresse?.toLowerCase().includes(search)
      );
    }

    // Filtre par statut
    if (statusFilter) {
      filtered = filtered.filter(prospect => prospect.status === statusFilter);
    }

    // Filtre par date de création
    if (dateFilter !== 'tous') {
      const now = new Date();
      let startDate, endDate;

      switch (dateFilter) {
        case 'ce_mois':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'mois_dernier':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case '3_mois':
          startDate = new Date();
          startDate.setMonth(startDate.getMonth() - 3);
          endDate = now;
          break;
        case 'personnalisee':
          if (customDateStart && customDateEnd) {
            startDate = new Date(customDateStart);
            endDate = new Date(customDateEnd);
            endDate.setHours(23, 59, 59, 999);
          }
          break;
      }

      if (startDate && endDate) {
        filtered = filtered.filter(prospect => {
          if (!prospect.created_at) return false;
          const prospectDate = new Date(prospect.created_at);
          return prospectDate >= startDate && prospectDate <= endDate;
        });
      }
    }

    // Tri par date de création (du plus récent au plus ancien)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });

    return filtered;
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'nouveau': return 'Nouveau';
      case 'rdv_planifie': return 'RDV planifié';
      case 'converti_client': return 'Converti en client';
      case 'abandonne': return 'Abandonné';
      default: return status;
    }
  };

  if (loading || !userLoaded) {
    return <div className="flex justify-center items-center h-64">Chargement...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">
          {currentUser?.role === 'admin' ? 'Vision Globale - Prospects' : 'Gestion des Prospects'}
        </h1>
        {(currentUser?.role === 'telepro' || currentUser?.role === 'admin') && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Prospect
          </Button>
        )}
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{prospects.length}</div>
            <div className="text-sm text-gray-600">Total prospects</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              {prospects.filter(p => p.status === 'nouveau').length}
            </div>
            <div className="text-sm text-gray-600">Nouveaux</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-orange-600">
              {prospects.filter(p => p.status === 'rdv_planifie').length}
            </div>
            <div className="text-sm text-gray-600">RDV planifiés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {prospects.filter(p => p.status === 'converti_client').length}
            </div>
            <div className="text-sm text-gray-600">Convertis</div>
          </CardContent>
        </Card>
      </div>

      {/* Recherche et Filtres */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Recherche et Filtres
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Recherche */}
            <div className="col-span-2">
              <Label>Rechercher un prospect</Label>
              <Input
                placeholder="Nom, prénom, entreprise, SIREN, téléphone, adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Filtre par statut */}
            <div>
              <Label>Statut</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="rdv_planifie">RDV planifié</option>
                <option value="converti_client">Converti en client</option>
                <option value="abandonne">Abandonné</option>
              </select>
            </div>

            {/* Filtre par période */}
            <div>
              <Label>Période de création</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="tous">Toutes les périodes</option>
                <option value="ce_mois">Ce mois</option>
                <option value="mois_dernier">Mois dernier</option>
                <option value="3_mois">3 derniers mois</option>
                <option value="personnalisee">Période personnalisée</option>
              </select>
            </div>
          </div>

          {/* Dates personnalisées */}
          {dateFilter === 'personnalisee' && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Résultats */}
          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {getFilteredProspects().length} prospect(s) trouvé(s)
              {searchTerm && ` pour "${searchTerm}"`}
            </span>
            {(searchTerm || statusFilter || dateFilter !== 'tous') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setDateFilter('tous');
                  setCustomDateStart('');
                  setCustomDateEnd('');
                }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table des prospects */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Prospects</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Entreprise</TableHead>
                <TableHead>Adresse</TableHead>
                <TableHead>Code Postal</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Énergie</TableHead>
                <TableHead>Statut</TableHead>
                {currentUser?.role === 'admin' && <TableHead>Créé par</TableHead>}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getFilteredProspects().map((prospect) => (
                <TableRow key={prospect.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {prospect.prenom} {prospect.nom}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {prospect.telephone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      {prospect.nom_entreprise && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          <span className="text-sm">{prospect.nom_entreprise}</span>
                        </div>
                      )}
                      {prospect.siren && (
                        <div className="text-xs text-gray-500">SIREN: {prospect.siren}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate max-w-[200px]">{prospect.adresse}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-center">
                      {prospect.code_postal || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {prospect.ville || '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {prospect.type_energie && (
                        <Badge variant="outline" className="text-xs mb-1">
                          {prospect.type_energie}
                        </Badge>
                      )}
                      {prospect.consommation_annuelle_euro > 0 && (
                        <div className="text-xs text-gray-600">
                          {prospect.consommation_annuelle_euro}€/an
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(prospect.status)}>
                      {getStatusLabel(prospect.status)}
                    </Badge>
                  </TableCell>
                  {currentUser?.role === 'admin' && (
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {prospect.created_by_name || 'Utilisateur inconnu'}
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    {(currentUser?.role === 'telepro' || currentUser?.role === 'admin') && (
                      <div className="flex space-x-1">
                        {prospect.status === 'nouveau' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openRdvDialog(prospect)}
                            className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                          >
                            <Calendar className="w-3 h-3 mr-1" />
                            RDV
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditDialog(prospect)}
                        >
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDeleteProspect(prospect.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Création de prospect */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Prospect</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Informations obligatoires */}
            <div className="space-y-4">
              <h3 className="font-semibold text-red-600">Informations obligatoires</h3>
              
              {/* Adresse avec suggestions */}
              <div className="relative">
                <Label>Adresse *</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => {
                    setFormData({...formData, adresse: e.target.value});
                    searchAddresses(e.target.value);
                  }}
                  placeholder="Commencez à taper l'adresse..."
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {addressSuggestions.map((address, index) => (
                      <div
                        key={index}
                        className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => selectAddress(address)}
                      >
                        {address.properties.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Code postal et Ville */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
              </div>
              
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>

            {/* Informations optionnelles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-blue-600">Informations optionnelles</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* SIREN avec recherche automatique */}
              <SirenSearch
                value={formData.siren}
                onChange={(siren) => setFormData({...formData, siren})}
                onCompanySelect={handleCompanySelect}
              />

              <div>
                <Label>Nom d'entreprise</Label>
                <Input
                  value={formData.nom_entreprise}
                  onChange={(e) => setFormData({...formData, nom_entreprise: e.target.value})}
                />
              </div>
            </div>

            {/* Informations énergie */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-semibold text-green-600">Informations Énergie</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type d'énergie</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.type_energie}
                    onChange={(e) => setFormData({...formData, type_energie: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="electricite">Électricité</option>
                    <option value="gaz">Gaz</option>
                    <option value="les_deux">Les deux</option>
                  </select>
                </div>
                
                <div>
                  <Label>Consommation annuelle (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_euro}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_euro: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Consommation annuelle (kWh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_kwh}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_kwh: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Fournisseur actuel</Label>
                <Input
                  value={formData.fournisseur_actuel}
                  onChange={(e) => setFormData({...formData, fournisseur_actuel: e.target.value})}
                  placeholder="EDF, Engie, Total Energies..."
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateProspect} className="bg-blue-600 hover:bg-blue-700">
              Créer le Prospect
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modification de prospect */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Prospect</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Informations obligatoires */}
            <div className="space-y-4">
              <h3 className="font-semibold text-red-600">Informations obligatoires</h3>
              
              {/* Adresse avec suggestions */}
              <div className="relative">
                <Label>Adresse *</Label>
                <Input
                  value={formData.adresse}
                  onChange={(e) => {
                    setFormData({...formData, adresse: e.target.value});
                    searchAddresses(e.target.value);
                  }}
                  placeholder="Commencez à taper l'adresse..."
                />
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {addressSuggestions.map((address, index) => (
                      <div
                        key={index}
                        className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => selectAddress(address)}
                      >
                        {address.properties.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Code postal et Ville */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    placeholder="Paris"
                  />
                </div>
              </div>
              
              <div>
                <Label>Téléphone *</Label>
                <Input
                  value={formData.telephone}
                  onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                  placeholder="01 23 45 67 89"
                />
              </div>
            </div>

            {/* Informations optionnelles */}
            <div className="space-y-4">
              <h3 className="font-semibold text-blue-600">Informations optionnelles</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>

              {/* SIREN avec recherche automatique */}
              <SirenSearch
                value={formData.siren}
                onChange={(siren) => setFormData({...formData, siren})}
                onCompanySelect={handleCompanySelect}
              />

              <div>
                <Label>Nom d'entreprise</Label>
                <Input
                  value={formData.nom_entreprise}
                  onChange={(e) => setFormData({...formData, nom_entreprise: e.target.value})}
                />
              </div>
            </div>

            {/* Informations énergie */}
            <div className="col-span-2 space-y-4">
              <h3 className="font-semibold text-green-600">Informations Énergie</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type d'énergie</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={formData.type_energie}
                    onChange={(e) => setFormData({...formData, type_energie: e.target.value})}
                  >
                    <option value="">Sélectionner...</option>
                    <option value="electricite">Électricité</option>
                    <option value="gaz">Gaz</option>
                    <option value="les_deux">Les deux</option>
                  </select>
                </div>
                
                <div>
                  <Label>Consommation annuelle (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_euro}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_euro: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label>Consommation annuelle (kWh)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.consommation_annuelle_kwh}
                    onChange={(e) => setFormData({...formData, consommation_annuelle_kwh: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Fournisseur actuel</Label>
                <Input
                  value={formData.fournisseur_actuel}
                  onChange={(e) => setFormData({...formData, fournisseur_actuel: e.target.value})}
                  placeholder="EDF, Engie, Total Energies..."
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateProspect} className="bg-blue-600 hover:bg-blue-700">
              Sauvegarder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Création de RDV avec Créneaux Optimisés */}
      <Dialog open={showRdvDialog} onOpenChange={setShowRdvDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Planifier un Rendez-vous
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {selectedProspectForRdv && (
                `Pour: ${selectedProspectForRdv.prenom} ${selectedProspectForRdv.nom} - ${selectedProspectForRdv.adresse}`
              )}
            </p>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Informations prospect */}
            {selectedProspectForRdv && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Contact:</span> {selectedProspectForRdv.prenom} {selectedProspectForRdv.nom}
                    </div>
                    <div>
                      <span className="font-medium">Téléphone:</span> {selectedProspectForRdv.telephone}
                    </div>
                    <div className="col-span-2">
                      <span className="font-medium">Adresse:</span> {selectedProspectForRdv.adresse}
                    </div>
                    {selectedProspectForRdv.nom_entreprise && (
                      <div className="col-span-2">
                        <span className="font-medium">Entreprise:</span> {selectedProspectForRdv.nom_entreprise}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bouton Créneaux Optimisés */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Intelligence Artificielle - Créneaux Optimisés
                  </h3>
                  <p className="text-sm text-blue-600 mt-1">
                    Trouvez automatiquement les meilleurs créneaux en fonction des temps de trajet et de la charge de travail des commerciaux
                  </p>
                </div>
                <Button 
                  onClick={openCreneauxDialog}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!selectedProspectForRdv?.latitude || !selectedProspectForRdv?.longitude}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Créneaux Disponibles
                </Button>
              </div>
              
              {(!selectedProspectForRdv?.latitude || !selectedProspectForRdv?.longitude) && (
                <div className="mt-2 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                  ⚠️ Adresse non géolocalisée. L'optimisation des créneaux nécessite une adresse complète.
                </div>
              )}
            </div>

            {/* Formulaire RDV classique */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-700">Ou saisir manuellement:</h3>
              
              <div>
                <Label>Date et heure du rendez-vous *</Label>
                <Input
                  type="datetime-local"
                  value={rdvFormData.date_rdv}
                  onChange={(e) => setRdvFormData({...rdvFormData, date_rdv: e.target.value})}
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Durée (minutes)</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={rdvFormData.duree_minutes}
                    onChange={(e) => setRdvFormData({...rdvFormData, duree_minutes: parseInt(e.target.value)})}
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 heure</option>
                    <option value={90}>1h30</option>
                    <option value={120}>2 heures</option>
                  </select>
                </div>

                <div>
                  <Label>Type de RDV</Label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={rdvFormData.type_rdv}
                    onChange={(e) => setRdvFormData({...rdvFormData, type_rdv: e.target.value})}
                  >
                    <option value="visite_commerciale">Visite commerciale</option>
                    <option value="telephone">Téléphonique</option>
                    <option value="visio">Visioconférence</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Commentaire</Label>
                <Textarea
                  value={rdvFormData.commentaire_rdv}
                  onChange={(e) => setRdvFormData({...rdvFormData, commentaire_rdv: e.target.value})}
                  placeholder="Notes sur le rendez-vous..."
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => {
              setShowRdvDialog(false);
              resetRdvForm();
              setSelectedProspectForRdv(null);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateRdv}
              disabled={!rdvFormData.date_rdv}
              className="bg-green-600 hover:bg-green-700"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Créer le RDV
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Créneaux Disponibles */}
      {selectedProspectForRdv && (
        <CreneauxDisponibles
          isOpen={showCreneauxDialog}
          onClose={() => setShowCreneauxDialog(false)}
          prospectAddress={selectedProspectForRdv.adresse}
          prospectLatitude={selectedProspectForRdv.latitude}
          prospectLongitude={selectedProspectForRdv.longitude}
          onSelectCreneau={handleSelectCreneau}
        />
      )}
    </div>
  );
};

export default ProspectsPage;